/**
 * Napojenie na autoritatívny zápas.
 *
 * Akcie idú cez REST (tam je transakcia, validácia aj zápis do logu), zmeny
 * stavu prichádzajú cez WebSocket — takže súper vidí ťah hneď, nie až keď sa
 * sám niečoho dotkne. Keď spojenie spadne, klient sa pripája znova s odstupom
 * a medzitým si stav dotiahne REST-om.
 */

import type { Action, GameEvent } from '@unmatched/engine';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError, matches, type MatchView } from '../api.js';

export type Connection = 'connecting' | 'online' | 'offline';

export interface ServerGame {
  view: MatchView | null;
  events: GameEvent[];
  error: string | null;
  loading: boolean;
  busy: boolean;
  connection: Connection;
  dispatch: (action: Action) => void;
  join: (heroId: string) => void;
  reload: () => void;
}

const RECONNECT_MS = [1000, 2000, 5000, 10_000];

/** Zlúčenie dvoch prúdov udalostí podľa `seq` — každá práve raz, v poradí. */
function mergeEvents(a: GameEvent[], b: GameEvent[]): GameEvent[] {
  const bySeq = new Map<number, GameEvent>();
  for (const event of a) bySeq.set(event.seq, event);
  for (const event of b) bySeq.set(event.seq, event);
  return [...bySeq.values()].sort((x, y) => x.seq - y.seq);
}

export function useServerGame(matchId: string): ServerGame {
  const [view, setView] = useState<MatchView | null>(null);
  const [events, setEvents] = useState<GameEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [connection, setConnection] = useState<Connection>('connecting');
  // Akcie posielame po jednej — dvojklik nesmie poslať dve naraz.
  const inFlight = useRef(false);
  const seenSeq = useRef(-1);

  /**
   * Udalosti prídu aj REST odpoveďou aj push-om; podľa `seq` ich zapíšeme raz.
   * Filtrovanie musí byť MIMO setState updatera — ten sa v StrictMode volá
   * dvakrát a posun `seenSeq` v ňom by druhý priebeh zahodil celý zoznam.
   */
  const appendEvents = useCallback((incoming: GameEvent[]) => {
    const fresh = incoming.filter((e) => e.seq > seenSeq.current);
    if (fresh.length === 0) return;
    seenSeq.current = Math.max(seenSeq.current, ...fresh.map((e) => e.seq));
    setEvents((log) => [...log, ...fresh]);
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    void (async () => {
      try {
        // Priebeh sa ťahá spolu so stavom — po refreshi je log inak prázdny,
        // hoci server históriu celý čas má.
        const [next, history] = await Promise.all([
          matches.get(matchId),
          matches.log(matchId).catch(() => ({ log: [] })),
        ]);
        setView(next);
        const past = history.log.flatMap((row) => row.events);
        if (past.length > 0) {
          seenSeq.current = Math.max(seenSeq.current, ...past.map((e) => e.seq));
          // Push cez WebSocket mohol doraziť skôr než odpoveď — zlúčime podľa `seq`.
          setEvents((log) => mergeEvents(past, log));
        }
        setError(null);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Zápas sa nepodarilo načítať');
      } finally {
        setLoading(false);
      }
    })();
  }, [matchId]);

  useEffect(load, [load]);

  // --- push kanál ----------------------------------------------------------
  useEffect(() => {
    let socket: WebSocket | null = null;
    let attempt = 0;
    let closed = false;
    let retry: ReturnType<typeof setTimeout> | undefined;

    const connect = () => {
      if (closed) return;
      setConnection(attempt === 0 ? 'connecting' : 'offline');
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      socket = new WebSocket(`${protocol}://${window.location.host}/ws?match=${matchId}`);

      socket.onopen = () => {
        attempt = 0;
        setConnection('online');
      };

      socket.onmessage = (message) => {
        try {
          const payload = JSON.parse(message.data as string) as {
            type: string;
            view?: MatchView;
            events?: GameEvent[];
            error?: string;
          };
          if (payload.type === 'state' && payload.view) {
            setView(payload.view);
            appendEvents(payload.events ?? []);
            setLoading(false);
          } else if (payload.type === 'error') {
            setError(payload.error ?? 'Chyba spojenia');
          }
        } catch {
          // poškodená správa sa dá ignorovať, stav si vypýtame pri ďalšom pushi
        }
      };

      socket.onclose = () => {
        if (closed) return;
        setConnection('offline');
        const delay = RECONNECT_MS[Math.min(attempt, RECONNECT_MS.length - 1)]!;
        attempt += 1;
        retry = setTimeout(connect, delay);
      };

      socket.onerror = () => socket?.close();
    };

    connect();
    return () => {
      closed = true;
      if (retry) clearTimeout(retry);
      socket?.close();
    };
  }, [matchId, appendEvents]);

  const dispatch = useCallback(
    (action: Action) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setBusy(true);
      void (async () => {
        try {
          const result = await matches.act(matchId, action);
          setView(result.view);
          appendEvents(result.events);
          setError(null);
        } catch (err) {
          setError(err instanceof ApiError ? err.message : 'Akcia neprešla');
          // Server je zdroj pravdy — po odmietnutej akcii si vypýtame jeho verziu.
          load();
        } finally {
          inFlight.current = false;
          setBusy(false);
        }
      })();
    },
    [matchId, appendEvents, load],
  );

  const join = useCallback(
    (heroId: string) => {
      setBusy(true);
      void (async () => {
        try {
          setView(await matches.join(matchId, heroId));
          setError(null);
        } catch (err) {
          setError(err instanceof ApiError ? err.message : 'Pripojenie zlyhalo');
        } finally {
          setBusy(false);
        }
      })();
    },
    [matchId],
  );

  return { view, events, error, loading, busy, connection, dispatch, join, reload: load };
}
